<template>
  <div class="e-image">
    <div class="container">
      <div class="control-panel">
        <div class="watermark-list">
          <button @click="triggerFileInput">上传图片</button>
          <input
              type="file"
              id="image-upload"
              accept=".png,.jpg,.jpeg"
              @change="onFileChange"
              style="display: none"
          >
        </div>

        <div class="control-group">
          <label for="quality">压缩质量 ({{ Math.round(quality * 100) }}%)</label>
          <input
              id="quality"
              type="range"
              class="slider"
              min="0.1"
              max="1"
              step="0.05"
              v-model="quality"
              @change="compressImage"
          >
        </div>

        <div class="control-group">
          <label for="ratio">尺寸缩放 ({{ ratio }}%)</label>
          <input
              id="ratio"
              type="range"
              class="slider"
              min="1"
              max="100"
              step="1"
              v-model="ratio"
              @change="compressImage"
          >
        </div>

        <div class="actions">
          <button class="btn" @click="reset">重置压缩参数</button>
          <button class="btn btn-success" @click="downloadImage" :disabled="!compressedImage">下载压缩后图片</button>
        </div>

      </div>

      <div class="right-panel">
        <div class="header" v-if="compressedImage">
          <div class="header-content">
            <div class="image-info-cards">

              <div class="info-card original">
                <div class="card-header">
                  <span class="icon">📷</span>
                  <span>原图信息</span>
                </div>
                <div class="info-item">
                  <span class="info-label">文件名:</span>
                  <span class="info-value">{{ originalFileName }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">文件大小:</span>
                  <span class="info-value">{{ formatFileSize(originalFileSize) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">图片尺寸:</span>
                  <span class="info-value">{{ originalDimensions.width }} × {{ originalDimensions.height }}</span>
                </div>
              </div>

              <div class="info-card compressed">
                <div class="card-header">
                  <span class="icon">✅</span>
                  <span>压缩后信息</span>
                </div>
                <div class="info-item">
                  <span class="info-label">体积减少:</span>
                  <span class="info-value reduction">{{ sizeReduction }}%</span>
                </div>
                <div class="info-item">
                  <span class="info-label">文件大小:</span>
                  <span class="info-value success">{{ formatFileSize(compressedFileSize) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">图片尺寸:</span>
                  <span class="info-value">{{ compressedDimensions.width }} × {{ compressedDimensions.height }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="canvas-container">
          <div v-if="!originalImage" class="upload-prompt">
            <div class="upload-icon">📷</div>
            <p>上传图片开始压缩</p>
          </div>

          <div v-else class="preview-comparison">
            <div class="preview-box">
              <canvas ref="compressedCanvas" class="preview-canvas"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EImage",
  data() {
    return {
      originalImage: null,
      compressedImage: null,
      originalFileName: '',
      originalFileSize: 0,
      compressedFileSize: 0,
      originalDimensions: { width: 0, height: 0 },
      compressedDimensions: { width: 0, height: 0 },
      quality: 0.8,
      ratio: 100
    };
  },
  computed: {
    sizeReduction() {
      if (!this.compressedFileSize) return 0;
      return Math.round(((this.originalFileSize - this.compressedFileSize) / this.originalFileSize) * 100);
    }
  },
  watch: {
    compressedImage() {
      this.$nextTick(() => {
        this.drawCompressedImage();
      });
    }
  },
  methods: {
    triggerFileInput() {
      document.getElementById('image-upload').click();
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    processFile(file) {
      // 检查文件类型
      if (!file.type.match('image/(png|jpeg|jpg)')) {
        alert('请选择PNG或JPG格式的图片文件');
        return;
      }

      // 检查文件大小（最大10MB）
      if (file.size > 10 * 1024 * 1024) {
        alert('文件大小不能超过10MB');
        return;
      }

      this.originalFileName = file.name;
      this.originalFileSize = file.size;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.originalImage = e.target.result;

        // 获取图片尺寸
        const img = new Image();
        img.onload = () => {
          this.originalDimensions = {
            width: img.width,
            height: img.height
          };
          // 上传图片后自动压缩
          this.compressImage();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    // 绘制压缩后的图片到canvas
    drawCompressedImage() {
      if (!this.compressedImage || !this.$refs.compressedCanvas) return;

      const canvas = this.$refs.compressedCanvas;
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // 获取容器尺寸
        const container = canvas.parentElement;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // 计算适合预览的尺寸，确保图片完全显示且不超出容器
        const maxWidth = Math.min(containerWidth - 40, 800); // 减去内边距
        const maxHeight = containerHeight - 40;

        let { width, height } = this.calculatePreviewSize(img.width, img.height, maxWidth, maxHeight);

        canvas.width = width;
        canvas.height = height;

        // 清除画布
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 绘制图片
        ctx.drawImage(img, 0, 0, width, height);
      };

      img.src = this.compressedImage;
    },

    // 计算预览尺寸（支持最大高度限制）
    calculatePreviewSize(originalWidth, originalHeight, maxWidth, maxHeight = Infinity) {
      // 计算宽度比例
      let widthRatio = maxWidth / originalWidth;
      // 计算高度比例
      let heightRatio = maxHeight / originalHeight;

      // 使用较小的比例，确保图片完全显示
      let ratio = Math.min(widthRatio, heightRatio, 1); // 不超过原始尺寸

      return {
        width: Math.round(originalWidth * ratio),
        height: Math.round(originalHeight * ratio)
      };
    },

    compressImage() {
      if (!this.originalImage) return;

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // 计算新尺寸，考虑缩放比例
        let newWidth = img.width * (this.ratio / 100);
        let newHeight = img.height * (this.ratio / 100);

        // 限制最大尺寸
        const maxWidth = 10000;
        const maxHeight = 10000;

        if (newWidth > maxWidth || newHeight > maxHeight) {
          const ratio = Math.min(maxWidth / newWidth, maxHeight / newHeight);
          newWidth *= ratio;
          newHeight *= ratio;
        }

        canvas.width = Math.floor(newWidth);
        canvas.height = Math.floor(newHeight);

        // 绘制图片到canvas
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // 获取压缩后的图片数据
        canvas.toBlob((blob) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.compressedImage = e.target.result;
            this.compressedFileSize = blob.size;
            this.compressedDimensions = {
              width: newWidth,
              height: newHeight
            };
          };
          reader.readAsDataURL(blob);
        }, 'image/jpeg', this.quality);
      };
      img.src = this.originalImage;
    },

    downloadImage() {
      if (!this.compressedImage) return;

      const link = document.createElement('a');
      link.download = `compressed_${this.originalFileName}`;
      link.href = this.compressedImage;
      link.click();
    },

    reset() {
      this.originalImage = null;
      this.compressedImage = null;
      this.originalFileName = '';
      this.originalFileSize = 0;
      this.compressedFileSize = 0;
      this.originalDimensions = { width: 0, height: 0 };
      this.compressedDimensions = { width: 0, height: 0 };
      this.quality = 0.8;
      this.ratio = 100;

      const fileInput = document.getElementById('image-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';

      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
}
</script>

<style lang="less" scoped>
.e-image {
  font-family: 'Arial', sans-serif;
  background-color: #f5f5f5;
  padding: 15px;
  display: flex;
  min-height: 90vh;

  .container {
    flex: 1;
    display: flex;
    gap: 20px;
    max-width: 95vw;
    margin: 0 auto;

    .control-panel {
      width: 300px;
      height: 90vh;
      overflow-y: auto;
      overflow-x: hidden;
      background-color: white;
      border-radius: 8px;
      padding: 20px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      gap: 20px;

      .watermark-list {
        margin-bottom: 7px;

        button {
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 8px;
          font-size: 15px;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-bottom: 12px;
          width: 100%;
        }

        button:hover {
          background-color: #3367d6;
        }
      }

      .control-group {
        display: flex;
        flex-direction: column;
        gap: 8px;

        label {
          font-weight: bold;
          color: #555;
          font-size: 14px;
        }

        input[type="range"] {
          width: 100%;
          padding: 0;
        }
      }

      .actions {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .btn {
          background-color: #4285f4;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 10px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;

          &:hover {
            background-color: #3367d6;
          }

          &:disabled {
            background-color: #bdc3c7;
            cursor: not-allowed;
          }

          &.btn-success {
            background-color: #2ecc71;

            &:hover {
              background-color: #27ae60;
            }
          }
        }
      }

      .result-info {
        background-color: #e8f4fc;
        border-left: 4px solid #3498db;
        padding: 15px;
        border-radius: 5px;
        margin-top: 10px;

        p {
          margin: 5px 0;
          font-size: 13px;
          color: #2c3e50;
        }
      }
    }

    .right-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 20px;
      min-height: 90vh;

      .header {
        background-color: white;
        border-radius: 8px;
        padding: 15px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

        .header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 15px;

          .image-info-cards {
            display: flex;
            gap: 20px;
            width: 100%;
            margin-top: 10px;
          }

          .info-card {
            flex: 1;
            padding: 15px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .info-card.original {
            background-color: #f8f9fa;
            border-left: 4px solid #4285f4;
          }

          .info-card.compressed {
            background-color: #f0f8f0;
            border-left: 4px solid #2ecc71;
          }

          .card-header {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: bold;
            font-size: 16px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
          }

          .card-header .icon {
            font-size: 18px;
          }

          .info-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .info-label {
            font-size: 13px;
            color: #666;
          }

          .info-value {
            font-size: 13px;
            font-weight: bold;
            color: #333;
          }

          .info-value.success {
            color: #2ecc71;
          }

          .info-value.ratio {
            color: #3498db;
          }

          .info-value.reduction {
            color: #e74c3c;
          }
        }
      }

      .canvas-container {
        background-color: white;
        border-radius: 8px;
        padding: 20px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        flex: 1;
        overflow: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 300px;

        .upload-prompt {
          text-align: center;
          color: #999;

          .upload-icon {
            font-size: 48px;
            margin-bottom: 15px;
          }

          p {
            font-size: 16px;
          }
        }

        .preview-comparison {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;

          .preview-box {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 10px;
            box-sizing: border-box;

            .preview-canvas {
              max-width: 100%;
              max-height: 100%;
              object-fit: contain;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              border-radius: 4px;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 900px) {
  .e-image .container {
    flex-direction: column;
  }

  .e-image .control-panel {
    width: 100%;
    height: auto;
    max-height: 50vh;
  }

  .e-image .right-panel {
    min-height: auto;
  }

  .e-image .canvas-container {
    max-height: 500px;
  }

  .e-image .preview-comparison {
    flex-direction: column;
    align-items: center;
  }

  .e-image .header-content {
    flex-direction: column;
    align-items: stretch !important;

    .image-info-cards {
      flex-direction: column;
    }
  }
}

@media (max-width: 600px) {
  .e-image .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>